import { useEntitiesFromSearch } from 'api/search'
import { useMemo, useState } from 'react'
import { useUsers } from '../../api/aws/useUsers'
import { aggregateStageTotals, formatCurrency } from './aggregateStageTotals'
import DealsKanban from './DealsKanban'
import './styles.css'

const updateDealStages = deals => {
  return deals?.map(deal => {
    const stageMap = {
      'Qualified Lead': 'Proposal',
      'New Lead': 'Lead',
      'Proposed - Cold': 'Cold',
      'Agreement Sent': 'Contract Sent',
    }
    if (stageMap[deal?.stage]) {
      deal.stage = stageMap[deal.stage]
    }
    return deal
  })
}

const calculateDaysSinceModified = lastModified => {
  if (!lastModified) return 'N/A'
  const modifiedDate = new Date(lastModified)
  const today = new Date()
  const timeDiff = today.getTime() - modifiedDate.getTime()
  return Math.floor(timeDiff / (1000 * 60 * 60 * 24))
}

const loadDealsIntoKanban = (deals, extraStages, users, organizations) => {
  const defaultStages = {
    Discovery: { items: [], label: 'Discovery' },
    'Pending Approval': { items: [], label: 'Pending Approval' },
    Proposal: { items: [], label: 'Proposal' },
    'Contract Created': { items: [], label: 'Contract Created' },
    'Contract Sent': { items: [], label: 'Contract Sent' },
    'Deal Won': { items: [], label: 'Deal Won' },
  }

  const additionalStages = {
    Cold: { items: [], label: 'Cold' },
    Purgatory: { items: [], label: 'Lost' },
  }

  const stages = extraStages ? { ...defaultStages, ...additionalStages } : defaultStages

  const sortedDeals = [...deals].sort((a, b) => {
    const dateA = new Date(a.lastModified || 0).getTime()
    const dateB = new Date(b.lastModified || 0).getTime()
    return dateB - dateA
  })

  sortedDeals.forEach(deal => {
    const owner = users.find(user => user.user_uuid === deal?.owner?.[0])
    const organization = organizations.find(org => org.org_uuid === deal?.organizations?.[0])

    if (stages[deal.stage]) {
      const daysSinceModified = calculateDaysSinceModified(deal.lastModified)
      stages[deal.stage].items.push({
        id: deal.id || deal.deal_uuid,
        primary: deal.name || deal.Deal_Name || deal.organizations?.name,
        secondary: `Stage: ${deal.stage} - Modified: ${daysSinceModified} days ago`,
        details: `Contact Email: ${deal.contact?.email || 'No contact email'}, Website: ${
          deal.contact?.websiteUrl || 'No website URL'
        }, Total Value: ${deal.total ? formatCurrency(deal.total) : '$0'}`,
        total: deal.total || 0,
        ownerName: owner?.name,
        lastModified: deal.lastModified,
        staleDays: daysSinceModified,
        ...deal,
        organization: {
          name: organization?.name,
          vertical: organization?.vertical,
        },
      })
    }
  })

  return stages
}

const KanbanContainer = () => {
  const { organizations, deals: pipelineDeals } = useEntitiesFromSearch()
  const { users, isUsersLoading } = useUsers(true, ['users'])
  const [extraStages, setExtraStages] = useState(false)

  const stableDeals = useMemo(() => pipelineDeals || [], [pipelineDeals])
  const stableUsers = useMemo(() => users || [], [users])
  const stableOrganizations = useMemo(() => organizations || [], [organizations])
  const refinedDeals = useMemo(() => updateDealStages(stableDeals), [stableDeals])

  const kanbanData = useMemo(() => {
    if (stableDeals.length === 0 || isUsersLoading) return {}
    return loadDealsIntoKanban(refinedDeals, extraStages, stableUsers, stableOrganizations)
  }, [refinedDeals, extraStages, stableUsers, stableOrganizations, isUsersLoading])

  const stageTotals = useMemo(() => aggregateStageTotals(kanbanData), [kanbanData])

  const kanbanTotals = useMemo(() => {
    const targetStages = ['Discovery', 'Pending Approval', 'Proposal', 'Contract Created', 'Contract Sent']
    let openDeals = 0
    let activePipelineIncome = 0
    let weightedPipeline = 0

    targetStages.forEach(stage => {
      if (kanbanData[stage]) {
        const items = kanbanData[stage].items
        openDeals += items.length
        items.forEach(deal => {
          const dealTotal = deal.total ? parseFloat(deal.total) : 0
          activePipelineIncome += dealTotal
          const probability = deal.probability ? parseFloat(deal.probability) : 50
          weightedPipeline += dealTotal * (probability / 100)
        })
      }
    })

    let dealWonRevenue = 0
    if (kanbanData['Deal Won']) {
      kanbanData['Deal Won'].items.forEach(deal => {
        dealWonRevenue += deal.total ? parseFloat(deal.total) : 0
      })
    }

    return {
      openDeals,
      activePipelineIncome,
      weightedPipeline: Math.round(weightedPipeline),
      dealWonRevenue,
    }
  }, [kanbanData])

  return (
    <DealsKanban
      kanbanData={kanbanData}
      stageTotals={stageTotals}
      extraStages={extraStages}
      setExtraStages={setExtraStages}
      totalDeals={kanbanTotals.openDeals}
      activePipeline={kanbanTotals.activePipelineIncome}
      weightedPipeline={kanbanTotals.weightedPipeline}
      dealWonRevenue={kanbanTotals.dealWonRevenue}
      isLoading={isUsersLoading}
    />
  )
}

export default KanbanContainer
